import { useEffect, useRef } from 'react';
import apiClient from '../api/client';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/**
 * Convert a URL-safe base64 VAPID key to a Uint8Array,
 * as required by PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output  = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/** True if the existing subscription was created with our CURRENT VAPID key. */
function subscriptionMatchesKey(sub, wantedKey) {
  const existing = sub?.options?.applicationServerKey;
  if (!existing) return false; // key unknown → treat as stale and re-subscribe
  const a = new Uint8Array(existing);
  if (a.length !== wantedKey.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== wantedKey[i]) return false;
  return true;
}

/**
 * usePushSubscription — subscribes the current device to Web Push Notifications.
 *
 * Call this once the user is authenticated (pass enabled=true).
 * Silently skips if:
 *   - REACT_APP_VAPID_PUBLIC_KEY is not set (development / not yet configured)
 *   - Browser does not support Push API
 *   - User denies notification permission
 *
 * On success: sends the PushSubscription object to POST /push/subscribe.
 * On re-render: the ref guard prevents double-subscription.
 */
/**
 * subscribeToPush — ensure this device has a push subscription registered with
 * the backend. Best triggered by a user gesture (e.g. a button click) so the
 * browser actually shows the "Allow notifications?" dialog. Throws a tagged
 * Error on the known failure modes so the caller can show a helpful message.
 */
export async function subscribeToPush() {
  if (!VAPID_PUBLIC_KEY) throw new Error('push_not_configured');
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    throw new Error('push_unsupported');
  }
  // Ask permission explicitly on the user gesture (reliable prompt).
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('permission_denied');
  } else if (Notification.permission === 'denied') {
    throw new Error('permission_denied');
  }
  const registration = await navigator.serviceWorker.ready;
  const wantedKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
  let sub = await registration.pushManager.getSubscription();

  // Heal a STALE subscription: if the browser already has one that was created
  // with a different (old) VAPID key, the push service rejects our sends with
  // "VAPID public key mismatch". Drop it (browser + backend) and re-subscribe
  // with the current key.
  if (sub && !subscriptionMatchesKey(sub, wantedKey)) {
    const oldEndpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    try { await apiClient.delete('/push/subscribe', { data: { endpoint: oldEndpoint } }); } catch { /* ignore */ }
    sub = null;
  }

  if (!sub) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: wantedKey,
    });
  }
  await apiClient.post('/push/subscribe', sub.toJSON());
  return sub;
}

export function usePushSubscription(enabled = false) {
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!enabled)          return;
    if (subscribedRef.current) return;
    if (!VAPID_PUBLIC_KEY) return; // not configured — skip silently
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    // Only auto-subscribe if permission was ALREADY granted — never trigger the
    // prompt on app load (browsers block gesture-less prompts anyway). The
    // Settings button handles first-time opt-in on a real user gesture.
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    subscribedRef.current = true;

    (async () => {
      try {
        await subscribeToPush();
        // (no logging: a browser console message is not a feature)
      } catch (err) {
        console.warn('[Push] Subscription skipped:', err.message);
        subscribedRef.current = false;
      }
    })();
  }, [enabled]);
}
